import dayjs, { type Dayjs } from 'dayjs';
import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { queryTestRuns, queryTestRunStatusSummary } from '@/features/executions/data';
import {
  buildStatusDeltaMap,
  buildStatusMetricCards,
  mapStatusOptionsToMetricOptions,
  resolveMetricTone,
} from '@/features/metrics/components/statusMetricCards';
import { useMetricsQuery } from '@/features/metrics/hooks/metrics';
import {
  buildTestCounts,
  type TestStatusKey,
} from '@/features/quality/components/qualityStatusDataHelpers';
import { formatRangeWindowNoDataText } from '@/features/quality/hooks';
import { EntityTrendsPageShell } from '@/features/shared/components/entityTrends/EntityTrendsPageShell';
import {
  resolveMetaDateBounds,
  type TableQueryMeta,
} from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import { useDateWindow } from '@/features/shared/hooks/useDateWindow';
import { useEntityTrendsPage } from '@/features/shared/hooks/useEntityTrendsPage';
import { useProjectStatusPresentation } from '@/features/shared/hooks/useProjectStatusPresentation';
import { resolveTestEntityDisplayName } from '@/features/shared/utils/testDisplayName';
import { useSuiteOptions } from '@/features/suites/hooks/useSuiteOptions';
import { TestRunsTable } from '@/features/tests/components/TestRunsTable';
import { TestsTable } from '@/features/tests/components/TestsTable';
import { queryTests, queryTestStatusSummary } from '@/features/tests/data/tests';
import { useTestsQuery } from '@/features/tests/hooks/tests';
import {
  testRunDefaultColumnVisibilityModel,
  testRunDefaultSortDirection,
  testRunDefaultSortKey,
  testRunSortFieldMap,
  type TestRunSortDirection,
  type TestRunSortKey,
} from '@/features/tests/pages/testRunsTableDefinition';
import { TestsPageExtendedContent } from '@/features/tests/pages/TestsPageExtendedContent';
import { TestsPageHistorySection } from '@/features/tests/pages/TestsPageHistorySection';
import { TestsPageTabbedPanel } from '@/features/tests/pages/TestsPageTabbedPanel';
import {
  type TestSortDirection,
  type TestSortKey,
  testsTableDefinition,
} from '@/features/tests/pages/testsTableDefinition';
import { useTestsPageFilterOptionCounts } from '@/features/tests/pages/useTestsPageFilterOptionCounts';
import { testRunSummaryStatusOptions } from '@/shared/constants/statusOptions';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { getLoadingMessage } from '@/shared/copy/loading';
import { gridPaths, queryKeys } from '@/shared/hooks';
import {
  buildDateRangeFilters,
  buildEqFilterValue,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { type GridColumnVisibilityModel } from '@/ui/primitives';
import type { ExecutionStatusKey } from '@/features/metrics/metricsHelpers';
import type { MetricsStatusChartPanelModelToolbarState } from '@/features/shared/components/chartPanels/useMetricsStatusChartPanelModel';
import type { DateQuickFilter } from '@/features/shared/hooks/useDateWindow';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  QueryResult,
  TestRunDto,
  TestRunStatus,
  TestRunStatusSummaryDto,
  TestListItemDto,
  TestSummaryDto,
} from '@flowqa/shared';

type TabbedTestsPanelTab = 'tests' | 'executions';

const isTestSortKey = (field: string | undefined): field is TestSortKey =>
  field !== undefined &&
  Object.prototype.hasOwnProperty.call(testsTableDefinition.sortFieldMap, field);

const areStringArraysEqual = (left: string[], right: string[]): boolean =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const toMetricsCountMap = (
  statusSummary: Array<{ key: ExecutionStatusKey; value: number }> | undefined,
): Partial<Record<ExecutionStatusKey, number>> =>
  Object.fromEntries((statusSummary ?? []).map((entry) => [entry.key, entry.value])) as Partial<
    Record<ExecutionStatusKey, number>
  >;

const sumMetricsCounts = (counts: Partial<Record<ExecutionStatusKey, number>>): number =>
  Object.values(counts).reduce((sum, value) => sum + value, 0);

const TestsPageInner = (): ReactElement => {
  const viewModel = useEntityTrendsPage<TestListItemDto, TestSummaryDto | TestRunStatusSummaryDto>({
    loadingMessage: getLoadingMessage('tests'),
    metricsScope: 'test',
    loadExecutionMetrics: false,
    gridPath: gridPaths.tests,
    executionGridPath: gridPaths.testRuns,
    entitySearchField: 'name',
    executionSearchField: 'testName',
    statusOptions: testRunSummaryStatusOptions.map((option) => ({
      value: option.value as Exclude<TestStatusKey, 'untested'>,
      label: option.label,
    })),
    summaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.testSummary(projectId, organisationId, filtersKey),
    executionSummaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.testRunSummary(projectId, organisationId, filtersKey),
    summaryPrefixKey: queryKeys.testSummaryPrefix,
    executionSummaryPrefixKey: queryKeys.testRunSummaryPrefix,
    queryLatestSummary: queryTestStatusSummary,
    queryExecutionSummary: queryTestRunStatusSummary,
    buildCounts: buildTestCounts,
    selectThresholds: (thresholds) => thresholds.tests,
    queryEntityTable: async (
      input: GridQueryInput,
      {
        selectedProjectId,
        selectedOrganisationId,
        rangeStartIso,
        rangeEndIso,
        previousRangeStartIso,
        previousRangeEndIso,
        deltasEnabled,
      },
    ): Promise<QueryResult<TestListItemDto>> =>
      queryTests(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        metricsStartDay: rangeStartIso ?? undefined,
        metricsEndDay: rangeEndIso ?? undefined,
        previousMetricsStartDay: previousRangeStartIso ?? undefined,
        previousMetricsEndDay: previousRangeEndIso ?? undefined,
        include: {
          suites: { ids: true, items: true, primary: true },
          latestRun: true,
          executionStatusCounts: true,
          executionSummaries: true,
          metrics: true,
          metricDeltas: deltasEnabled && previousRangeStartIso !== null,
        },
      }),
  });
  const { preferFileNameOverDisplayName } = useProjectStatusPresentation();
  const dailyHistoryDateRangeLabel = useMemo(() => {
    if (!viewModel.pickerStartDay || !viewModel.pickerEndDay) return null;
    return `${dayjs(viewModel.pickerStartDay).format('MMM D, YYYY')} - ${dayjs(viewModel.pickerEndDay).format('MMM D, YYYY')}`;
  }, [viewModel.pickerEndDay, viewModel.pickerStartDay]);
  const [panelActiveTab, setPanelActiveTab] = useState<TabbedTestsPanelTab>('tests');
  const initialPanelColumnVisibilityModel: GridColumnVisibilityModel = {
    ...testsTableDefinition.defaultColumnVisibilityModel,
  };
  const initialPanelExecutionColumnVisibilityModel: GridColumnVisibilityModel = {
    ...testRunDefaultColumnVisibilityModel,
  };

  const [panelDateQuickFilter, setPanelDateQuickFilter] = useState<DateQuickFilter>('7d');
  const [panelFromDate, setPanelFromDate] = useState<Dayjs | null>(null);
  const [panelToDate, setPanelToDate] = useState<Dayjs | null>(null);
  const [debouncedPanelSearch, setDebouncedPanelSearch] = useState('');
  const [panelColumnVisibilityModel, setPanelColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(initialPanelColumnVisibilityModel);
  const [panelSortKey, setPanelSortKey] = useState<TestSortKey>(
    testsTableDefinition.defaultSortKey,
  );
  const [panelSortDirection, setPanelSortDirection] = useState<TestSortDirection>(
    testsTableDefinition.defaultSortDirection,
  );
  const [panelFilterStatuses, setPanelFilterStatuses] = useState<TestRunStatus[]>([]);
  const [panelFilterTests, setPanelFilterTests] = useState<string[]>([]);
  const [panelFilterSuites, setPanelFilterSuites] = useState<string[]>([]);
  const [panelFilterTags, setPanelFilterTags] = useState<string[]>([]);
  const [panelExecutionColumnVisibilityModel, setPanelExecutionColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(initialPanelExecutionColumnVisibilityModel);
  const [panelExecutionSortKey, setPanelExecutionSortKey] =
    useState<TestRunSortKey>(testRunDefaultSortKey);
  const [panelExecutionSortDirection, setPanelExecutionSortDirection] =
    useState<TestRunSortDirection>(testRunDefaultSortDirection);
  const [panelTestsMeta, setPanelTestsMeta] = useState<TableQueryMeta<TestListItemDto> | null>(
    null,
  );
  const [panelTestRunsMeta, setPanelTestRunsMeta] = useState<TableQueryMeta<TestRunDto> | null>(
    null,
  );
  const filterOptionsEnabled = panelTestsMeta !== null || panelTestRunsMeta !== null;
  const { suiteOptions } = useSuiteOptions({
    projectId: viewModel.selectedProjectId,
    enabled: filterOptionsEnabled,
  });
  const testsOptionsQuery = useTestsQuery(viewModel.selectedProjectId, {
    enabled: filterOptionsEnabled,
  });
  const testOptions = useMemo(
    () =>
      (testsOptionsQuery.data?.items ?? []).map((test) => ({
        value: test.id,
        label: resolveTestEntityDisplayName(test, { preferFileNameOverDisplayName }),
        projectId: test.projectId,
      })),
    [preferFileNameOverDisplayName, testsOptionsQuery.data?.items],
  );
  const panelAvailableTags = useMemo(
    () =>
      Array.from(new Set(viewModel.tagOptions.map((option) => option.value))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [viewModel.tagOptions],
  );
  const panelAvailableTagOptions = useMemo(
    () => panelAvailableTags.map((tag) => ({ value: tag, label: tag })),
    [panelAvailableTags],
  );
  const toolbarStatusFilterOptions = useMemo(
    () =>
      viewModel.statusFilterOptions.filter(
        (option): option is { value: TestRunStatus; label: string } => option.value !== 'untested',
      ),
    [viewModel.statusFilterOptions],
  );
  const handleDebouncedPanelSearchChange = useCallback((value: string) => {
    setDebouncedPanelSearch(value);
  }, []);
  const showPanelDeltas = viewModel.deltasEnabled;
  const panelDateWindow = useDateWindow({
    dateQuickFilter: panelDateQuickFilter,
    fromDate: panelFromDate,
    toDate: panelToDate,
  });
  const { countedTestOptions, countedSuiteOptions, countedStatusOptions, countedTagOptions } =
    useTestsPageFilterOptionCounts({
      enabled: filterOptionsEnabled,
      selectedProjectId: viewModel.selectedProjectId,
      selectedOrganisationId: viewModel.selectedOrganisationId ?? null,
      search: debouncedPanelSearch,
      selectedStatuses: panelFilterStatuses,
      selectedTests: panelFilterTests,
      selectedSuites: panelFilterSuites,
      selectedTags: panelFilterTags,
      startDay: panelDateWindow.startDay,
      endDay: panelDateWindow.endDay,
      testOptions,
      suiteOptions,
      statusOptions: toolbarStatusFilterOptions,
      tagOptions: panelAvailableTagOptions,
    });
  const panelQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<TestListItemDto>> => {
      const mappedFilters = input.filters.map((filter) =>
        filter.field === 'statusFilter'
          ? {
              ...filter,
              field: 'executionStatus',
            }
          : filter,
      );
      const toolbarFilters = mergeFilters(
        buildInFilter('executionStatus', panelFilterStatuses),
        buildInFilter('id', panelFilterTests),
        buildInFilter('suiteId', panelFilterSuites),
        buildInFilter('tags', panelFilterTags),
      );
      const requestedSort = input.sorts.length > 0 ? (input.sorts[0] ?? null) : null;
      const requestedSortKey = isTestSortKey(requestedSort?.field)
        ? requestedSort.field
        : panelSortKey;
      const sortOverride: ApiSort = {
        field: testsTableDefinition.sortFieldMap[requestedSortKey],
        direction: requestedSort?.direction ?? panelSortDirection,
      };
      return queryTests(
        viewModel.selectedProjectId,
        viewModel.selectedOrganisationId ?? undefined,
        {
          ...input,
          sorts: [sortOverride],
          filters: [...mappedFilters, ...toolbarFilters],
          metricsStartDay: panelDateWindow.startDay ?? undefined,
          metricsEndDay: panelDateWindow.endDay ?? undefined,
          previousMetricsStartDay: panelDateWindow.previousStartDay ?? undefined,
          previousMetricsEndDay: panelDateWindow.previousEndDay ?? undefined,
          include: {
            suites: { ids: true, items: true, primary: true },
            latestRun: true,
            executionStatusCounts: true,
            executionSummaries: true,
            metrics: true,
            metricDeltas: showPanelDeltas,
          },
        },
      );
    },
    [
      panelDateWindow.endDay,
      panelDateWindow.previousEndDay,
      panelDateWindow.previousStartDay,
      panelDateWindow.startDay,
      panelFilterStatuses,
      panelFilterTests,
      panelFilterSuites,
      panelFilterTags,
      panelSortDirection,
      panelSortKey,
      showPanelDeltas,
      viewModel.selectedOrganisationId,
      viewModel.selectedProjectId,
    ],
  );
  const panelExecutionQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<TestRunDto>> => {
      const isLatestQuickFilter =
        panelDateQuickFilter === 'latest' && panelFromDate === null && panelToDate === null;
      const baseFilters = isLatestQuickFilter
        ? input.filters.filter(
            (filter) =>
              filter.field !== 'timestamp' &&
              filter.field !== 'createdAt' &&
              filter.field !== 'isLatest',
          )
        : input.filters;
      const toolbarFilters = mergeFilters(
        buildInFilter('status', panelFilterStatuses),
        buildInFilter('testId', panelFilterTests),
        buildInFilter('suiteId', panelFilterSuites),
        buildInFilter('tags', panelFilterTags),
        isLatestQuickFilter
          ? buildEqFilterValue('isLatest', true)
          : buildDateRangeFilters(
              'timestamp',
              panelDateWindow.startDay
                ? dayjs(panelDateWindow.startDay).startOf('day').toISOString()
                : null,
              panelDateWindow.endDay
                ? dayjs(panelDateWindow.endDay).endOf('day').toISOString()
                : null,
            ),
      );
      const sortOverride: ApiSort = {
        field: testRunSortFieldMap[panelExecutionSortKey],
        direction: panelExecutionSortDirection,
      };
      return queryTestRuns(
        viewModel.selectedProjectId,
        viewModel.selectedOrganisationId ?? undefined,
        {
          ...input,
          search: debouncedPanelSearch,
          sorts: [sortOverride],
          filters: [...baseFilters, ...toolbarFilters],
        },
      );
    },
    [
      panelDateWindow.endDay,
      panelDateWindow.startDay,
      panelDateQuickFilter,
      panelFromDate,
      panelToDate,
      panelExecutionSortDirection,
      panelExecutionSortKey,
      panelFilterStatuses,
      panelFilterTests,
      panelFilterSuites,
      panelFilterTags,
      debouncedPanelSearch,
      viewModel.selectedOrganisationId,
      viewModel.selectedProjectId,
    ],
  );
  const testsEmptyState = getEmptyStateMessage('entity.noneFound', { entity: 'test' });
  const testRunsEmptyState = getEmptyStateMessage('entity.noneFound', { entity: 'testRun' });
  const testsRangeEmptyState = getEmptyStateMessage('entity.noneForSelectedRange', {
    entity: 'test',
  });
  const testRunsRangeEmptyState = getEmptyStateMessage('entity.noneForSelectedRange', {
    entity: 'testRun',
  });
  const panelNoRowsText = panelDateWindow.hasCustomBounds
    ? testsRangeEmptyState
    : formatRangeWindowNoDataText(testsEmptyState, panelDateQuickFilter, false);
  const panelExecutionsNoRowsText = panelDateWindow.hasCustomBounds
    ? testRunsRangeEmptyState
    : formatRangeWindowNoDataText(testRunsEmptyState, panelDateQuickFilter, false);
  const panelChartNoRowsText = panelExecutionsNoRowsText;
  const panelStatusFilterKey = useMemo(
    () => [...panelFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [panelFilterStatuses],
  );
  const selectedTestMetricEntityId = useMemo(
    () => (panelFilterTests.length === 1 ? (panelFilterTests[0] ?? null) : null),
    [panelFilterTests],
  );
  const hasCustomMetricDateRange = panelFromDate !== null || panelToDate !== null;
  const isLatestOnlyMetricMode =
    panelDateQuickFilter === 'latest' && panelFromDate === null && panelToDate === null;
  const showMetricDelta =
    panelDateQuickFilter !== 'latest' &&
    panelDateWindow.previousStartDay !== null &&
    panelDateWindow.previousEndDay !== null;
  const testMetricsQuery = useMetricsQuery({
    range: hasCustomMetricDateRange || isLatestOnlyMetricMode ? 'all' : panelDateQuickFilter,
    statuses: panelFilterStatuses as ExecutionStatusKey[],
    tags: panelFilterTags,
    scope: 'test',
    projectId: viewModel.selectedProjectId,
    entityId: selectedTestMetricEntityId,
    name: debouncedPanelSearch.trim().length > 0 ? debouncedPanelSearch : null,
    startDay: hasCustomMetricDateRange ? (panelFromDate?.format('YYYY-MM-DD') ?? null) : null,
    endDay: hasCustomMetricDateRange ? (panelToDate?.format('YYYY-MM-DD') ?? null) : null,
    latestOnly: isLatestOnlyMetricMode,
    enabled: selectedTestMetricEntityId !== null,
    refetchOnWindowFocus: false,
  });
  const previousTestMetricsQuery = useMetricsQuery({
    range: 'all',
    statuses: panelFilterStatuses as ExecutionStatusKey[],
    tags: panelFilterTags,
    scope: 'test',
    projectId: viewModel.selectedProjectId,
    entityId: selectedTestMetricEntityId,
    name: debouncedPanelSearch.trim().length > 0 ? debouncedPanelSearch : null,
    startDay: showMetricDelta ? panelDateWindow.previousStartDay : null,
    endDay: showMetricDelta ? panelDateWindow.previousEndDay : null,
    enabled: selectedTestMetricEntityId !== null && showMetricDelta,
    refetchOnWindowFocus: false,
  });
  const testMetricCards = useMemo(() => {
    if (selectedTestMetricEntityId === null) {
      return viewModel.metricCards;
    }
    const counts = toMetricsCountMap(testMetricsQuery.data?.statusSummary);
    const previousCounts = toMetricsCountMap(previousTestMetricsQuery.data?.statusSummary);
    const totalSecondaryValue = viewModel.metricCards.find(
      (card) => card.id === 'total',
    )?.secondaryValue;
    return buildStatusMetricCards({
      options: mapStatusOptionsToMetricOptions(toolbarStatusFilterOptions, resolveMetricTone),
      counts,
      deltas: showMetricDelta ? buildStatusDeltaMap(counts, previousCounts) : undefined,
      total: sumMetricsCounts(counts),
      previousCounts,
      previousTotal: showMetricDelta ? sumMetricsCounts(previousCounts) : undefined,
      loading:
        testMetricsQuery.data === undefined &&
        testMetricsQuery.isFetching &&
        !testMetricsQuery.isError,
      showDelta: showMetricDelta,
      totalLabel: 'Test Runs',
      totalSecondaryValue,
    });
  }, [
    previousTestMetricsQuery.data?.statusSummary,
    selectedTestMetricEntityId,
    showMetricDelta,
    testMetricsQuery.data,
    testMetricsQuery.isError,
    testMetricsQuery.isFetching,
    toolbarStatusFilterOptions,
    viewModel.metricCards,
  ]);
  const panelSuiteFilterKey = useMemo(
    () => [...panelFilterSuites].sort((left, right) => left.localeCompare(right)).join(','),
    [panelFilterSuites],
  );
  const panelTestFilterKey = useMemo(
    () => [...panelFilterTests].sort((left, right) => left.localeCompare(right)).join(','),
    [panelFilterTests],
  );
  const panelTagFilterKey = useMemo(
    () => [...panelFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [panelFilterTags],
  );
  useEffect(() => {
    if (viewModel.searchValue !== debouncedPanelSearch) {
      viewModel.handleSearchChange(debouncedPanelSearch);
    }
    if (!areStringArraysEqual(viewModel.selectedStatuses, panelFilterStatuses)) {
      viewModel.handleStatusesChange(panelFilterStatuses as TestStatusKey[]);
    }
    if (!areStringArraysEqual(viewModel.selectedTags, panelFilterTags)) {
      viewModel.handleTagsChange(panelFilterTags);
    }
    if (viewModel.range !== panelDateQuickFilter) {
      viewModel.handleRangeChange(panelDateQuickFilter);
    }
    if (panelFromDate === null && panelToDate === null) {
      if (viewModel.hasCustomDates) {
        viewModel.handleSelectedStartDayChange(null);
        viewModel.handleSelectedEndDayChange(null);
      }
      return;
    }
    const nextStartDay = panelFromDate ? panelFromDate.format('YYYY-MM-DD') : null;
    const nextEndDay = panelToDate ? panelToDate.format('YYYY-MM-DD') : null;
    if (nextStartDay !== null && viewModel.pickerStartDay !== nextStartDay) {
      viewModel.handleSelectedStartDayChange(nextStartDay);
    }
    if (nextEndDay !== null && viewModel.pickerEndDay !== nextEndDay) {
      viewModel.handleSelectedEndDayChange(nextEndDay);
    }
  }, [
    panelDateQuickFilter,
    panelFilterStatuses,
    panelFilterTags,
    panelFromDate,
    panelToDate,
    debouncedPanelSearch,
    viewModel,
  ]);
  const panelTestRunDateBounds = useMemo(
    () => resolveMetaDateBounds(panelTestRunsMeta, (item) => item.timestamp),
    [panelTestRunsMeta],
  );
  const panelTestLatestDateBounds = useMemo(
    () => resolveMetaDateBounds(panelTestsMeta, (item) => item.latestRun?.timestamp ?? null),
    [panelTestsMeta],
  );
  const historyLatestDateBounds = useMemo(
    () =>
      panelTestLatestDateBounds.from !== null || panelTestLatestDateBounds.to !== null
        ? panelTestLatestDateBounds
        : panelTestRunDateBounds,
    [panelTestRunDateBounds, panelTestLatestDateBounds],
  );
  const sharedChartToolbarState = useMemo<MetricsStatusChartPanelModelToolbarState<TestRunStatus>>(
    () => ({
      search: debouncedPanelSearch,
      setSearch: (value) => {
        handleDebouncedPanelSearchChange(value);
        viewModel.handleSearchChange(value);
      },
      selectedStatuses: panelFilterStatuses,
      setSelectedStatuses: (next) => {
        setPanelFilterStatuses(next);
        viewModel.handleStatusesChange(next as TestStatusKey[]);
      },
      selectedEntities: panelFilterTests,
      setSelectedEntities: setPanelFilterTests,
      entityOptions: countedTestOptions,
      entityLabel: 'Test',
      selectedSuites: panelFilterSuites,
      setSelectedSuites: setPanelFilterSuites,
      suiteOptions: countedSuiteOptions,
      suiteLabel: 'Suite',
      selectedProjects: viewModel.selectedProjects,
      setSelectedProjects: viewModel.handleProjectsChange,
      projectOptions: viewModel.showProjectFilter ? viewModel.projectFilterOptions : undefined,
      selectedTags: panelFilterTags,
      setSelectedTags: (next) => {
        setPanelFilterTags(next);
        viewModel.handleTagsChange(next);
      },
      noRowsText: panelChartNoRowsText,
      latestQueryStartDay:
        historyLatestDateBounds.from?.format('YYYY-MM-DD') ?? viewModel.pickerStartDay,
      latestQueryEndDay: historyLatestDateBounds.to?.format('YYYY-MM-DD') ?? viewModel.pickerEndDay,
      dateQuickFilter: panelDateQuickFilter,
      onDateQuickFilterChange: (next) => {
        setPanelDateQuickFilter(next);
        setPanelFromDate(null);
        setPanelToDate(null);
        viewModel.handleRangeChange(next);
        viewModel.handleSelectedStartDayChange(null);
        viewModel.handleSelectedEndDayChange(null);
      },
      fromDate: panelFromDate,
      toDate: panelToDate,
      onFromDateChange: (value) => {
        const nextValue = value ? dayjs(value) : null;
        setPanelFromDate(nextValue);
        viewModel.handleSelectedStartDayChange(nextValue ? nextValue.format('YYYY-MM-DD') : null);
      },
      onToDateChange: (value) => {
        const nextValue = value ? dayjs(value) : null;
        setPanelToDate(nextValue);
        viewModel.handleSelectedEndDayChange(nextValue ? nextValue.format('YYYY-MM-DD') : null);
      },
    }),
    [
      panelDateQuickFilter,
      panelFilterStatuses,
      panelFilterSuites,
      panelFilterTests,
      panelFilterTags,
      panelFromDate,
      panelChartNoRowsText,
      panelToDate,
      debouncedPanelSearch,
      handleDebouncedPanelSearchChange,
      historyLatestDateBounds.from,
      historyLatestDateBounds.to,
      viewModel,
      countedSuiteOptions,
      countedTestOptions,
      countedStatusOptions,
      countedTagOptions,
    ],
  );
  const extendedContentOverride = (
    <TestsPageExtendedContent
      groupErroredWithFailed={viewModel.groupErroredWithFailed}
      panelAvailableTags={panelAvailableTags}
      countedAvailableTags={countedTagOptions}
      tabbedPanelProps={{
        panelActiveTab,
        setPanelActiveTab,
        panelDateQuickFilter,
        setPanelDateQuickFilter,
        panelFromDate,
        setPanelFromDate,
        panelToDate,
        setPanelToDate,
        panelColumnVisibilityModel,
        setPanelColumnVisibilityModel,
        panelSortKey,
        setPanelSortKey,
        panelSortDirection,
        setPanelSortDirection,
        panelFilterStatuses,
        setPanelFilterStatuses,
        panelFilterTests,
        setPanelFilterTests,
        panelFilterSuites,
        setPanelFilterSuites,
        panelFilterTags,
        setPanelFilterTags,
        panelExecutionColumnVisibilityModel,
        setPanelExecutionColumnVisibilityModel,
        panelExecutionSortKey,
        setPanelExecutionSortKey,
        panelExecutionSortDirection,
        setPanelExecutionSortDirection,
        panelAvailableTags,
        suiteOptions: countedSuiteOptions,
        testOptions: countedTestOptions,
        panelDateWindow,
        panelStatusFilterKey,
        panelTestFilterKey,
        panelSuiteFilterKey,
        panelTagFilterKey,
        panelNoRowsText,
        panelExecutionsNoRowsText,
        debouncedPanelSearch,
        panelTestsMeta,
        setPanelTestsMeta,
        panelTestRunsMeta,
        setPanelTestRunsMeta,
        panelTestRunDateBounds,
        showPanelDeltas,
        selectedOrganisationId: viewModel.selectedOrganisationId,
        selectedProjectId: viewModel.selectedProjectId,
        handleSearchChange: viewModel.handleSearchChange,
        handleRangeChange: viewModel.handleRangeChange,
        handleSelectedStartDayChange: viewModel.handleSelectedStartDayChange,
        handleSelectedEndDayChange: viewModel.handleSelectedEndDayChange,
        handleStatusesChange: viewModel.handleStatusesChange,
        handleTagsChange: viewModel.handleTagsChange,
        handleDebouncedPanelSearchChange,
        panelQueryFn,
        panelExecutionQueryFn,
        tagOptionsOverride: viewModel.tagOptions,
      }}
      historySectionProps={{
        panelDateQuickFilter,
        panelDateWindow,
        panelFilterStatuses,
        panelFilterSuites,
        panelFilterTags,
        panelFilterTests,
        panelNoRowsText,
        panelSearchValue: debouncedPanelSearch,
        dateRangeLabel: dailyHistoryDateRangeLabel,
        latestDateBounds: historyLatestDateBounds,
        selectedOrganisationId: viewModel.selectedOrganisationId,
        selectedProjectId: viewModel.selectedProjectId,
        showForcedLoadingState: viewModel.showForcedLoadingState,
      }}
      onStatusToggle={(status) => {
        if (status === 'total') return;
        setPanelFilterStatuses((prev) =>
          prev.includes(status as TestRunStatus)
            ? prev.filter((item) => item !== status)
            : [...prev, status as TestRunStatus],
        );
        viewModel.handleStatusToggle(status as TestStatusKey);
      }}
      sharedChartToolbarState={sharedChartToolbarState}
      showLineChartPoints={viewModel.showLineChartPoints}
      showMetricCardsSkeleton={viewModel.showMetricCardsSkeleton}
      suppressChartAnimation={viewModel.suppressChartAnimation}
      testMetricCards={testMetricCards}
      TestsPageTabbedPanelComponent={TestsPageTabbedPanel}
      TestsPageHistorySectionComponent={TestsPageHistorySection}
      testsStatusOptions={countedStatusOptions}
    />
  );

  return (
    <EntityTrendsPageShell
      viewModel={viewModel}
      title="Tests"
      addButtonLabel="Add Test"
      searchAriaLabel="Search tests"
      chartTabsAriaLabel="Tests chart tabs"
      pageTabsAriaLabel="Tests page tabs"
      entityTabLabel="Tests"
      chartEmptyStateTitle={testRunsEmptyState}
      entityTableNoRowsText={testsEmptyState}
      executionTableNoRowsText={testRunsEmptyState}
      contentOverride={extendedContentOverride}
      renderEntityTable={({ showDeltas, noRowsText }) => (
        <TestsTable
          persistKey="tests-table"
          queryKey={viewModel.queryKey}
          queryFn={viewModel.queryFn}
          tagOptionsOverride={viewModel.tagOptions}
          initialPageSize={5}
          showDeltas={showDeltas}
          fixedFilters={viewModel.entityTableFilters}
          search={viewModel.debouncedSearchValue}
          noRowsText={noRowsText}
        />
      )}
      renderExecutionsTable={({ noRowsText }) => (
        <TestRunsTable
          persistKey="test-runs-table"
          fixedFilters={mergeFilters(viewModel.executionFilters, viewModel.executionStatusFilters)}
          tagOptionsOverride={viewModel.tagOptions}
          initialPageSize={5}
          search={viewModel.debouncedSearchValue}
          noRowsText={noRowsText}
        />
      )}
    />
  );
};

export const TestsPage = (): ReactElement => {
  return <TestsPageInner />;
};
